import DataTableColumnHeader from '@/components/datatable/DataTableColumnHeader.vue';
import DataTablePayrollInput from '@/components/datatable/DataTablePayrollInput.vue';
import PayrollTableComment from '@/components/salary/PayrollTableComment.vue';
import { useDateFormat } from '@/composables/useDateFormat';
import type { Employee, PayrollDataField } from '@/types';
import type { ColumnDef } from '@tanstack/vue-table';
import { h } from 'vue';

const { formatDMYFromIso } = useDateFormat();

export function createPayrollColumns(options: {
    editableFields?: PayrollDataField[];
    payrollId?: number;
    isApproved: boolean;
}) {
    const { editableFields = [], payrollId, isApproved } = options;

    // Base columns that are always present
    const baseColumns: ColumnDef<Employee>[] = [
        {
            accessorKey: 'status_text',
            header: ({ column }) =>
                h(DataTableColumnHeader, {
                    column: column,
                    title: 'salary.payroll.table_headings.status',
                }),
        },
        {
            accessorKey: 'name',
            header: ({ column }) =>
                h(DataTableColumnHeader, {
                    column: column,
                    title: 'salary.payroll.table_headings.name',
                }),
        },
        {
            accessorKey: 'employment_date',
            header: ({ column }) =>
                h(DataTableColumnHeader, {
                    column: column,
                    title: 'salary.payroll.table_headings.employment_date',
                }),
            cell: ({ row }) => {
                return formatDMYFromIso(row.original.employment_date);
            },
            filterFn: 'dateRange',
            meta: {
                filterType: 'dateRange',
            },
        },
        {
            accessorKey: 'termination',
            header: ({ column }) =>
                h(DataTableColumnHeader, {
                    column: column,
                    title: 'salary.payroll.table_headings.termination',
                }),
            cell: ({ row }) => {
                return formatDMYFromIso(row.original.termination?.termination_date);
            },
            filterFn: 'dateRange',
            meta: {
                filterType: 'dateRange',
            },
        },
    ];

    // Dynamic columns for editable fields
    const editableFieldColumns: ColumnDef<Employee>[] = [];

    editableFields.forEach((field) => {
        // Add units column if enabled
        if (field.units) {
            editableFieldColumns.push({
                accessorKey: `${field.name}_units`,
                header: ({ column }) =>
                    h(DataTableColumnHeader, {
                        column: column,
                        title: `salary.payroll.table_headings.${field.name}_units`,
                    }),
                cell: ({ row, table }) => {
                    return h(DataTablePayrollInput, {
                        value: row.original?.[`${field.name}_units`] ?? null,
                        editableField: row.original.payroll_data.find(
                            (data: PayrollDataField) => data.payroll_data_field_id === field.id,
                        ),
                        readOnly: row.original?.[`${field.name}_units_read_only`] ?? false,
                        employeeId: row.original.id,
                        field: field.name,
                        fieldType: 'units',
                        payrollId: payrollId,
                        isApproved: isApproved,
                    });
                },
            });
        }

        // Add rate column if enabled
        if (field.rate) {
            editableFieldColumns.push({
                accessorKey: `${field.name}_rate`,
                header: ({ column }) =>
                    h(DataTableColumnHeader, {
                        column: column,
                        title: `salary.payroll.table_headings.${field.name}_rate`,
                    }),
                cell: ({ row, table }) => {
                    return h(DataTablePayrollInput, {
                        value: row.original?.[`${field.name}_rate`] ?? null,
                        editableField: row.original.payroll_data.find(
                            (data: PayrollDataField) => data.payroll_data_field_id === field.id,
                        ),
                        readOnly: row.original?.[`${field.name}_rate_read_only`] ?? false,
                        employeeId: row.original.id,
                        field: field.name,
                        fieldType: 'rate',
                        payrollId: payrollId,
                        isApproved: isApproved,
                    });
                },
            });
        }

        // Add amount column if enabled
        if (field.amount) {
            editableFieldColumns.push({
                accessorKey: `${field.name}_amount`,
                header: ({ column }) =>
                    h(DataTableColumnHeader, {
                        column: column,
                        title: `salary.payroll.table_headings.${field.name}_amount`,
                    }),
                cell: ({ row, table }) => {
                    return h(DataTablePayrollInput, {
                        value: row.original?.[`${field.name}_amount`] ?? null,
                        editableField: row.original.payroll_data.find(
                            (data: PayrollDataField) => data.payroll_data_field_id === field.id,
                        ),
                        readOnly: row.original?.[`${field.name}_amount_read_only`] ?? false,
                        employeeId: row.original.id,
                        field: field.name,
                        fieldType: 'amount',
                        payrollId: payrollId,
                        isApproved: isApproved,
                    });
                },
            });
        }
    });

    // Final columns
    const finalColumns: ColumnDef<Employee>[] = [
        ...baseColumns,
        ...editableFieldColumns,
        {
            id: 'comment',
            accessorKey: 'comment',
            enableHiding: false,
            header: ({ column }) =>
                h(DataTableColumnHeader, {
                    column: column,
                    title: 'salary.payroll.table_headings.comment',
                }),
            cell: ({ row }) => {
                return h(PayrollTableComment, {
                    employee: row.original,
                    comment: row.original.comment,
                    isApproved: isApproved,
                    payrollId: payrollId,
                });
            },
        },
    ];

    return finalColumns;
}

// Keep the default export for backward compatibility
export const columns = createPayrollColumns({
    isApproved: false,
});
